import { Column, Table } from '@tanstack/react-table'
import langDe from 'i18n/de/resource.json'
import { TableColumnExportField, TableColumnMeta } from 'utils/table/tableColumnWidths'
import {
    buildExportHeadersList,
    ExportHeadersListEntry,
} from 'utils/table/exportHeaders'

const TABLE_UI_ONLY_COLUMNS = new Set(['select', 'actions'])

const resolveTranslation = (labelKey: string): string => langDe[labelKey as keyof typeof langDe] ?? labelKey

export const exportField = (key: string, labelKey: string): TableColumnExportField => ({
    key,
    labelKey,
})

export const exportColumn = (...fields: TableColumnExportField[]): Pick<TableColumnMeta, 'exportFields'> => ({
    exportFields: fields,
})

const getColumnExportFields = (column: Column<any>): TableColumnExportField[] => {
    const exportFields = (column.columnDef.meta as TableColumnMeta | undefined)?.exportFields
    return exportFields ?? []
}

const collectExportFieldsFromColumns = (columns: Column<any>[]): TableColumnExportField[] => {
    const fields: TableColumnExportField[] = []
    const seenKeys = new Set<string>()

    columns.forEach((column) => {
        if (TABLE_UI_ONLY_COLUMNS.has(column.id)) {
            return
        }

        getColumnExportFields(column).forEach((field) => {
            if (seenKeys.has(field.key)) {
                return
            }

            seenKeys.add(field.key)
            fields.push(field)
        })
    })

    return fields
}

/** Visible table columns → backend export fields, in UI column order. */
export const getVisibleExportFieldsFromTableColumns = (table: Table<any>): TableColumnExportField[] =>
    collectExportFieldsFromColumns(table.getVisibleLeafColumns())

/** Every export field declared on table columns that is not currently visible. */
export const getHiddenExportColumnsFromTableColumns = (table: Table<any>): string[] => {
    const visibleKeys = new Set(getVisibleExportFieldsFromTableColumns(table).map((field) => field.key))
    const allKeys = collectExportFieldsFromColumns(table.getAllLeafColumns()).map((field) => field.key)

    return allKeys.filter((key) => !visibleKeys.has(key))
}

const buildExportFieldLabelMap = (table: Table<any>): Map<string, string> => {
    const labelByKey = new Map<string, string>()

    table.getAllLeafColumns().forEach((column) => {
        getColumnExportFields(column).forEach((field) => {
            if (!labelByKey.has(field.key)) {
                labelByKey.set(field.key, field.labelKey)
            }
        })
    })

    return labelByKey
}

export const buildTableExportPayloadFromTable = (
    table?: Table<any>
): {
    hidden_columns?: string[]
    headers_list?: ExportHeadersListEntry[]
} => {
    if (!table) {
        return {}
    }

    const visibleFields = getVisibleExportFieldsFromTableColumns(table)
    const labelByKey = buildExportFieldLabelMap(table)

    return {
        hidden_columns: getHiddenExportColumnsFromTableColumns(table),
        headers_list: buildExportHeadersList(
            visibleFields.map((field) => field.key),
            (fieldName) => resolveTranslation(labelByKey.get(fieldName) ?? fieldName)
        ),
    }
}
